// ─────────────────────────────────────────────
//  Directive grammar — the sentences the board sends to a phone.
//
//  Pure: no React, no Firestore, no clock. The composer renders what this
//  builds and the payload is built from the SAME call, so the sentence an
//  operator approves and the sentence a clinician reads are provably one
//  string. Lifted out of TimelineTaskCard.js when the card passed 500 lines and
//  the verb list stopped being a list.
//
//  Six rules run through this file. Each is argued where it is enforced:
//  ⚠️ EVERY FORM IS AN INSTRUCTION OR A PERMISSION — never a report that
//     something happened. See THE VOICE.
//  ⚠️ A DIRECTIVE MAY NEVER NAME A ROOM IT DOES NOT HAVE. See WHERE.
//  ⚠️ A TIME MAY CROSS TO THE OTHER HALF ONLY AS A CLOCK. See WHEN.
//  ⚠️ WHAT IS TRUE OF THE EVENT CROSSES; WHAT IS AIMED AT ONE PERSON DOES NOT.
//     See THE SPLIT.
//  ⚠️ A SINGLE-SEAT VERB SENT TO TWO PEOPLE IS A DISPATCH ERROR. See `seats`.
//  ⚠️ BOTH SIDES OF A HANDOVER ARE TOLD, IN DIFFERENT WORDS — except a
//     cancellation and a room hold, which are single shared facts. See `mirror`.
//
//  ── THE VOICE ────────────────────────────────
//
//  Second person, present or future, always an order or a permission. "Take
//  over from Jim Wright" is an instruction; "you have taken over" is a claim
//  about the world that the board would then act on. The board records who is
//  ASSIGNED; it never observes who is standing in the room. So a sentence here
//  may describe the record ("you now hold WOR 5 on the board") and may instruct
//  an action ("hand over to her"), and may never assert that the action
//  happened.
//
//  ⚠️ "THEY", NEVER "HE"/"SHE". A staff record carries no gender; guessing one
//  is a fact the board does not have. Singular "they" also reads correctly when
//  more than one person was instructed, which is why `be` is passed in.
//
//  ── WHERE ────────────────────────────────────
//
//  ⚠️ THE ROOM IS A FIELD, NOT A SENTENCE FRAGMENT SOMEBODY TYPES. Every
//  automatic directive once said "your room" — the one thing a directive must
//  never be vague about — because the emitter read a field that did not exist
//  (taskEmitters.js documents the incident, reported live 2026-08-17). The
//  manual lane then shipped with the same defect by a different route: the verb
//  had a bare form ("Take over the room."), so the room could only live in free
//  text, and free text is optional.
//
//  Verbs that cannot be obeyed without a place carry `needsWhere`, and the send
//  is REFUSED without one. Their bare form does not invent a room and does not
//  stay silent — it says the board has not named one and tells the reader to
//  ring back, so the preview is honest while the operator is still choosing.
//
//  For `transfer` the place is a CAMPUS, not an OR — see `whereKind`.
//
//  ── WHEN ─────────────────────────────────────
//
//  ⚠️ A CLOCK TIME, NEVER A PHRASE. "after this case" is deictic: the arriving
//  provider reads it as THEIR current case and the departing one reads it as
//  THEIRS. Same six words, two different instants, and the two clinicians walk
//  toward each other at different times. A clock time is the same instant for
//  both people, which is the only reason a time is allowed to cross into the
//  counterpart's half at all. A relative phrase is only true for the person it
//  was written at, so it belongs in Detail, which does not cross.
//
//  ⚠️ AND THE TWO HALVES WORD IT DIFFERENTLY. An instruction with a time is an
//  order; a report of somebody ELSE's time is a promise, and a promise the board
//  cannot keep is what makes a clinician hand over to an empty room. So the
//  arriving half reads "at 3:30 PM" and the waiting half reads "planned for
//  3:30 PM" followed by the gate — do not act on the clock, act on the person
//  arriving. An ABSENT time is said out loud for the same reason: silence there
//  reads as "now".
//
//  ── THE SPLIT ────────────────────────────────
//
//  ⚠️ WHAT IS TRUE OF THE EVENT MAY CROSS. WHAT IS AIMED AT ONE PERSON MAY NOT.
//  Where and when are facts about the event, so both halves carry them. Detail
//  is prose written in the second person at the recipient ("WOR 12, after this
//  case"); pasted under "Arjana Ray is taking over from you." it reads as an
//  order to the wrong clinician, and that clinician could move rooms because of
//  a line addressed to somebody else.
//
//  The exception is `detailIsShared`, and it is an exception to the REASON, not
//  a hole in the rule. For a room hold and a cancellation the detail is not an
//  instruction to anybody — it is the cause ("blood is not in the building"),
//  a fact about the room, equally true for everyone standing in it. A stop order
//  whose cause reached only half the room is a stop order that gets negotiated.
//
//  Before this split the counterpart got neither where nor when, which was its
//  own failure: the person being relieved read "Arjana Ray is taking over from
//  you." with no room and no clock. An attending covering four rooms learns
//  almost nothing from that sentence.
// ─────────────────────────────────────────────
import { MAX_TASK_TEXT } from "./tasks";

// ── THE CLOCK ────────────────────────────────
//
// `when` is "HH:MM" 24-hour, the value an <input type="time"> produces. It is
// rendered as 12-hour prose because that is how a notification reads on a phone
// in this hospital — domain/tasks makes the same argument for `formatStampTime`
// ("at 9:41 PM is how a notification reads and at 21:41 is not").
//
// ⚠️ PARSED STRICTLY, AND AN UNPARSEABLE VALUE IS NO TIME AT ALL. A browser that
// hands back something else, or a value restored from a stale draft, must not
// reach a phone as "at NaN:undefined PM" beside a clinical instruction. No time
// is safe here; a wrong clock is not.
export function formatWhen(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  if (!m) return "";
  const h24 = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isInteger(h24) || !Number.isInteger(min)) return "";
  if (h24 < 0 || h24 > 23 || min < 0 || min > 59) return "";
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(min).padStart(2, "0")} ${h24 < 12 ? "AM" : "PM"}`;
}

// The arriving half: an order. " at 3:30 PM" / "".
const atClause = (when) => {
  const t = formatWhen(when);
  return t ? ` at ${t}` : "";
};

// The waiting half: a plan, plus the gate. Never empty — an absent time is
// stated, because absence reads as "now" to somebody watching a door.
const plannedClause = (when) => {
  const t = formatWhen(when);
  return t ? `, planned for ${t}` : " — no time set yet";
};

// " in WOR 5" / "". A helper so no verb can accidentally write "in the room"
// when `where` is absent; the absent case is empty, and the verbs that cannot
// tolerate an absent place carry `needsWhere`.
function inWhere(where) {
  const w = String(where || "").trim();
  return w ? ` in ${w}` : "";
}

const named = (name) => String(name || "").trim();

// ── CALL POSITIONS ───────────────────────────
//
// ⚠️ THE POSITION IS PICKED, NEVER ASSUMED. An earlier draft of the call verb
// said "You are first call from now" for every use, because the composer had no
// field for a call number — a fact stated by a system that does not have it,
// which is the same class of mistake as "your room". The board DOES hold the
// number (`roster.supervisors[].callOrder`), so the honest fix is to ask for it
// and put it in the sentence, not to point the reader at a screen they are
// trying not to have to open.
//
// ⚠️ NO `Trauma call` IN THE DEFAULT LIST. It exists only at trauma centres, and
// a picker that offers it where it does not exist invites somebody to select it.
// This list belongs in facility config eventually; it is defaulted here so the
// verb can ship, and a facility that runs a different call structure needs this
// list, not a code change.
export const CALL_POSITIONS = {
  md: ["First call", "Second call", "Third call", "OB call", "Late room / relief call", "Backup (jeopardy) call"],
  provider: ["First CRNA call", "Second CRNA call", "OB CRNA call"],
};

export function callPositionsFor(role) {
  return role === "anesthesiologist" ? CALL_POSITIONS.md : CALL_POSITIONS.provider;
}

// Every position, deduped, for a picker that does not yet know the role.
export function allCallPositions() {
  return Array.from(new Set([...CALL_POSITIONS.md, ...CALL_POSITIONS.provider]));
}

// ── THE VERBS ────────────────────────────────
//
// Each entry:
//   id           stable string, written into no document — the TEXT is the
//                payload, so a verb can be renamed without a migration.
//   label        what the operator picks.
//   group        optgroup heading. A flat list of thirteen verbs is a list
//                nobody reads to the bottom of; the groups are the order a
//                board runner thinks in — cover the room, release the person,
//                stop the room, move between campuses.
//   counterpart  the MEANING of the third control for THIS verb, shown under
//                the row. One picker means "relieving whom" for a takeover and
//                "relieved by whom" for a departure, and an operator who
//                guesses wrong sends the two providers the wrong way round.
//                `null` for a one-sided verb, which HIDES the control rather
//                than leaving it enabled and meaningless.
//   seats        1 = the verb names ONE seat, so two recipients means two
//                people each told to fill it. "many" = true of each recipient
//                independently. See `directiveWarnings`.
//   needsWhere   the send is refused without a place.
//   needsDetail  the send is refused without a reason.
//   whereKind    "room" (today's OR list) or "campus" (the other facilities).
//   needsPosition the send is refused without a call position.
//   detailIsShared the detail is a fact about the event, so it crosses to the
//                counterpart's half. See THE SPLIT.
//   mirror       the counterpart reads the SAME sentence, deliberately.
//   say          the recipient's half.
//   tell         the counterpart's half, or null for a one-sided verb.
//
// ⚠️ THE DIRECTION OF EACH `tell` IS FIXED BY `counterpart`, NOT BY THE VERB.
// For "Take over" the counterpart is the one being relieved; for "Go home" the
// counterpart is the RELIEVER. Their halves therefore point opposite ways. In
// every `tell`, `who` is the PEOPLE BEING INSTRUCTED and the counterpart is the
// person reading it — a `tell` that names the counterpart back to themselves is
// the reversal this whole shape exists to prevent, and a test asserts it.
export const DIRECTIVE_ACTIONS = [
  // ── Cover the room ──
  {
    id: "break",
    label: "Give a break",
    group: "Cover the room",
    counterpart: "who gets the break",
    seats: 1,
    needsWhere: true,
    whereKind: "room",
    // The bare form used to be "Give a break." — give whom, where. It is the
    // most-used verb on the card and the one whose recipient acts fastest,
    // which makes it the one whose vagueness costs the most.
    say: ({ name, where, when }) =>
      where
        ? `Give ${named(name) ? `${name} ` : ""}a break in ${where}${atClause(when)}.`
        : "Give a break — the board has not named the room. Call the board before you move.",
    tell: ({ who, be, where, when }) =>
      `${who} ${be} coming to give you a break${inWhere(where)}${plannedClause(when)}.`,
  },
  {
    id: "take_over",
    label: "Take over",
    group: "Cover the room",
    counterpart: "relieving whom",
    seats: 1,
    needsWhere: true,
    whereKind: "room",
    say: ({ name, where, when }) =>
      where
        ? `Take over${named(name) ? ` from ${name}` : ""} in ${where}${atClause(when)}.`
        : "Take over a room the board has not named — call the board now.",
    // ⚠️ "ASSIGNED TO TAKE OVER", NOT "IS TAKING OVER". Reliefs are staged up to
    // ninety minutes ahead, and the present progressive reads as happening now.
    // The automatic lane already says "is staged to relieve you"; this lane said
    // "is taking over from you" for the same event. One vocabulary, or the two
    // lanes are two systems — and on a phone they are one.
    //
    // ⚠️ AND THE GATE IS THE PERSON, NOT THE CLOCK. "Do not hand over until they
    // are in the room" is what stops a handover to an empty room when an ETA
    // slips, which is the failure a time in this half could otherwise cause.
    tell: ({ who, be, where, when }) =>
      `${who} ${be} assigned to take over from you${inWhere(where)}${plannedClause(when)}. Do not hand over until they are in the room.`,
  },
  {
    id: "next_room",
    label: "Next room",
    group: "Cover the room",
    counterpart: "who they relieve there",
    seats: 1,
    needsWhere: true,
    whereKind: "room",
    say: ({ name, where, when }) =>
      where
        ? named(name)
          ? `After this room you are relieving ${name} in ${where}${atClause(when)}.`
          : `After this room you are moving to ${where}${atClause(when)}.`
        : "After this room the board will move you — call the board for the room.",
    // ⚠️ A PROMISE WITH NO CLOCK, SAID AS A PROMISE WITH NO CLOCK. The person
    // reading this decides whether to start another case on it. The old wording
    // ("relieving you after their current room") sounded like a schedule; it is
    // an intention, and the last sentence says so rather than implying it.
    tell: ({ who, be, when }) =>
      `${who} ${be} assigned to relieve you when their current room finishes${plannedClause(when)}. Do not count on a time until they are in the room.`,
  },
  {
    id: "stat",
    label: "Come now — extra hands",
    group: "Cover the room",
    counterpart: null,
    seats: 1,
    needsWhere: true,
    whereKind: "room",
    // ⚠️ THIS IS NOT THE EMERGENCY ALERT AND MUST NEVER BECOME ONE. The
    // emergency lane broadcasts to every supervisor and owns the red/blink
    // layer. This is narrower and just as time-critical: one named person, come
    // here now — a difficult airway, a crash section, a second pair of hands for
    // a line. It rides the ordinary quiet task channel. Alarm colour is finite;
    // spend it here and staff learn to ignore the colour that means someone is
    // dying.
    say: ({ where }) =>
      where
        ? `Come to ${where} now. Extra hands needed.`
        : "Come to the board now. Extra hands needed — the board will tell you the room.",
    tell: null,
  },
  {
    id: "hold",
    label: "Stand by — do not leave",
    group: "Cover the room",
    counterpart: null,
    // True of every recipient independently: five people can each be told to
    // stay without any of them being told to do the same single job.
    seats: "many",
    needsWhere: false,
    whereKind: "room",
    say: ({ when }) => {
      const t = formatWhen(when);
      return t
        ? `Stay in the department until at least ${t} — do not leave until the board releases you.`
        : "Stay in the department — do not leave until the board releases you.";
    },
    tell: null,
  },

  // ── Release the person ──
  {
    id: "go_home_after_case",
    label: "Go home after this case",
    group: "Release the person",
    counterpart: "relieved by whom",
    seats: "many",
    needsWhere: false,
    whereKind: "room",
    say: ({ name, when }) =>
      named(name)
        ? `You are released to go home after your current case, once ${name} relieves you${atClause(when)}. Tell the board when you are leaving.`
        : `You are released to go home after your current case${atClause(when)}. Tell the board when you are leaving.`,
    tell: ({ who, where, when }) =>
      `You are relieving ${who}${inWhere(where)} at the end of their case so they can go home${plannedClause(when)}.`,
  },
  {
    id: "go_home",
    label: "Go home",
    group: "Release the person",
    counterpart: "relieved by whom",
    seats: "many",
    needsWhere: false,
    whereKind: "room",
    // ⚠️ THE BARE FORM CARRIES ITS OWN CONDITION. "You are released to go home."
    // reaches a CRNA who is at that moment holding an open abdomen, and nothing
    // in the sentence says "when your room is covered". Some people will walk.
    say: ({ name, when }) =>
      named(name)
        ? `You are released to go home once ${name} relieves you${atClause(when)}.`
        : "You are released to go home once your room is covered. Do not leave a room uncovered.",
    // The counterpart here is the INCOMING reliever, so their half is the duty,
    // not the permission — the permission belongs to the person leaving.
    tell: ({ who, where, when }) =>
      `You are relieving ${who}${inWhere(where)} so they can go home${plannedClause(when)}.`,
  },

  // ── Stop the room ──
  {
    id: "hold_room",
    label: "Hold the room — do not induce",
    group: "Stop the room",
    counterpart: "who else to tell",
    // Everyone in the room is genuinely told the same true thing: the room is
    // stopped. That is one fact about a place, not one job.
    seats: "many",
    needsWhere: true,
    whereKind: "room",
    // ⚠️ THE REASON IS REQUIRED, AND THAT IS A CLINICAL DECISION, NOT A FORM
    // PREFERENCE. A stop order without a cause gets negotiated at the door. The
    // cause is also what tells the room what to DO while it waits: blood not in
    // the building means somebody rings blood bank; consent means fetch the
    // surgeon; a wrong-site concern means nobody touches anything. "Do not
    // induce" alone gives them no next action.
    needsDetail: true,
    detailLabel: "Reason — required",
    detailPlaceholder: "e.g. Blood is not in the building",
    // The cause is a fact about the room, true for everyone in it.
    detailIsShared: true,
    mirror: true,
    // ⚠️ A STOP ORDER, AND THE ONLY DIRECTIVE THAT MUST BE READ BEFORE THE NEXT
    // THING HAPPENS. It has its own verb precisely so it cannot arrive as a
    // paragraph of free text that reads like everything else.
    say: ({ where }) =>
      where
        ? `Hold ${where} — do not induce until the board releases the room.`
        : "Hold the room — do not induce until the board releases the room. Call the board now to confirm which room.",
    tell: ({ where }) =>
      where
        ? `Hold ${where} — do not induce until the board releases the room.`
        : "Hold the room — do not induce until the board releases the room. Call the board now to confirm which room.",
  },
  {
    id: "release_room",
    label: "Release the room — you may proceed",
    group: "Stop the room",
    counterpart: "who else to tell",
    seats: "many",
    needsWhere: true,
    whereKind: "room",
    detailIsShared: true,
    mirror: true,
    // ⚠️ A SEPARATE, DELIBERATE SEND TO THE SAME PEOPLE, NEVER AN EXPIRY. A hold
    // that lifted itself after some interval would be a stop order the board
    // stopped enforcing without anybody deciding to. Somebody says "go".
    say: ({ where }) =>
      where
        ? `${where} is released — the hold is lifted and you may proceed.`
        : "A room hold has been lifted — call the board to confirm which room before you proceed.",
    tell: ({ where }) =>
      where
        ? `${where} is released — the hold is lifted and you may proceed.`
        : "A room hold has been lifted — call the board to confirm which room before you proceed.",
  },
  {
    id: "cancel",
    label: "Disregard — that instruction is cancelled",
    group: "Stop the room",
    counterpart: "who else was told",
    seats: "many",
    needsWhere: false,
    whereKind: "room",
    detailIsShared: true,
    mirror: true,
    // ⚠️ THE ONE OTHER LEGITIMATELY SYMMETRIC MESSAGE, AND THE REASON IT IS SO.
    // Every handover verb tells the two sides different words because they are
    // doing different jobs. A cancellation gives them no job at all: both are
    // being told the same single fact, that a thing they were both told is off.
    // Saying it differently to each would invite one of them to think their half
    // still stands.
    //
    // It exists because Clear does not retract — Clear hides rows on ONE board
    // and reaches no phone, and says so on screen. Without this verb, an
    // instruction sent thirty seconds before a case was cancelled could only be
    // undone by voice.
    say: ({ where }) =>
      `Disregard the last message${where ? ` about ${where}` : ""} — that instruction is cancelled.`,
    tell: ({ where }) =>
      `Disregard the last message${where ? ` about ${where}` : ""} — that instruction is cancelled.`,
  },

  // ── Move between campuses, and the desk ──
  {
    id: "transfer",
    label: "Transfer to another campus",
    group: "Move and admin",
    counterpart: "who they relieve there",
    seats: 1,
    needsWhere: true,
    // ⚠️ THE PLACE HERE IS A CAMPUS, NOT AN OR. This is a multi-campus product;
    // the directive lane is facility-scoped for exactly that reason, and the one
    // verb that is ABOUT crossing campuses used to name no campus at all. At
    // 17:30, "another facility" plus a twenty-five minute drive in the wrong
    // direction is a room left uncovered at both ends.
    whereKind: "campus",
    say: ({ name, where, when }) =>
      where
        ? named(name)
          ? `You are moving to ${where} to relieve ${name}${atClause(when)}.`
          : `You are moving to ${where}${atClause(when)}.`
        : "You are moving to another campus — call the board for which one before you travel.",
    // ⚠️ `origin` IS WHERE THE TRAVELLER IS COMING FROM, NOT WHERE THEY ARE
    // GOING. The counterpart is at the far end: they already know what building
    // they are standing in, and what they cannot know is how long the drive is.
    // Naming the destination here is noise at best, and at worst reads as though
    // THEY are the one being moved.
    tell: ({ who, be, origin, when }) =>
      `${who} ${be} travelling${origin ? ` from ${origin}` : ""} to relieve you${plannedClause(when)}. Do not leave until they arrive.`,
  },
  {
    id: "call_change",
    label: "On call from now",
    group: "Move and admin",
    counterpart: "who comes off call",
    seats: "many",
    needsWhere: false,
    whereKind: "room",
    needsPosition: true,
    say: ({ position }) =>
      position
        ? `You are ${position.toLowerCase()} from now. Carry your phone.`
        : "You are on call from now — the board has not named which call. Call the board to confirm.",
    // ⚠️ NEVER "YOU ARE OFF CALL". That asserts they have no call at all, which
    // is false the moment somebody merely moved from first to second. "You are
    // no longer first call" is true in every case, which is the whole test.
    tell: ({ who, be, position }) =>
      position
        ? `You are no longer ${position.toLowerCase()}. ${who} ${be} ${position.toLowerCase()} from now.`
        : `${who} ${be} taking call from now.`,
  },
  {
    id: "call_board",
    label: "Call the board",
    group: "Move and admin",
    counterpart: null,
    seats: "many",
    needsWhere: false,
    whereKind: "room",
    say: () => "Call the board when you get a moment.",
    tell: null,
  },
];

export function actionById(id) {
  return DIRECTIVE_ACTIONS.find((a) => a.id === id) || null;
}

// The optgroup headings, in the order the verbs were declared. Derived rather
// than listed twice — a second list is a second thing to keep in step.
export function directiveGroups() {
  const out = [];
  DIRECTIVE_ACTIONS.forEach((a) => {
    const found = out.find((g) => g.group === a.group);
    if (found) found.actions.push(a);
    else out.push({ group: a.group, actions: [a] });
  });
  return out;
}

// "Ana", "Ana and Ben", "Ana, Ben and Dr. Rivera". Written out rather than
// counted ("2 providers are coming") because the counterpart has to recognise
// the person walking through the door.
export function nameList(names) {
  const list = (names || []).map((n) => String(n || "").trim()).filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

// ── THE ONE PLACE BOTH TEXTS ARE BUILT ───────
//
// The preview and the payload both call this, so the sentences the operator
// reads and the ones the phones show are provably the same strings. Detail
// whitespace is COLLAPSED (the phone renders it in a paragraph, where a newline
// shows as a space anyway); the words are never rewritten. Truncation is HERE,
// not in buildManualTask, or the preview would show 300 and the phone 280 — and
// the trailing trim matters, because a slice landing on a space is trimmed
// downstream, leaving the two one character apart.
//
// `recipientNames` are the people who will ACTUALLY be told — resolved,
// reachable ones only. Naming somebody with no app account as "coming to relieve
// you" would describe a relief to the one person who could be left waiting for
// it, since the reliever never received the instruction.
export function composeDirective({
  action = "",
  counterpartName = "",
  detail = "",
  recipientNames = [],
  where = "",
  when = "",
  origin = "",
  position = "",
} = {}) {
  const name = named(counterpartName);
  const place = String(where || "").trim();
  const body = String(detail || "").trim().replace(/\s+/g, " ");
  const verb = actionById(action);
  const ctx = { name, where: place, when, origin, position: String(position || "").trim() };

  const head = verb ? verb.say(ctx) : "";
  const text = [head, body].filter(Boolean).join(" ").slice(0, MAX_TASK_TEXT).trimEnd();

  const told = (recipientNames || []).map((n) => String(n || "").trim()).filter(Boolean);
  const who = nameList(told);

  // ⚠️ A MIRRORED VERB NEEDS NO COUNTERPART NAME IN ITS OWN SENTENCE, BUT IT
  // STILL NEEDS SOMEBODY TO SEND IT TO. `hold_room`, `release_room` and `cancel`
  // never interpolate `who`, so the usual guard (a name AND somebody being
  // instructed) would be the wrong question for them: what they need is a chosen
  // counterpart. Every other verb's `tell` reads `who`, so it needs both.
  const hasSecondHalf = Boolean(verb && verb.tell && name && (verb.mirror || who));
  const tellHead = hasSecondHalf
    ? verb.tell({ ...ctx, who, be: told.length > 1 ? "are" : "is" })
    : "";
  // The detail crosses ONLY for the verbs where it is a fact about the event.
  const tellBody = hasSecondHalf && verb.detailIsShared ? body : "";
  const counterpartText = hasSecondHalf
    ? [tellHead, tellBody].filter(Boolean).join(" ").slice(0, MAX_TASK_TEXT).trimEnd()
    : "";

  return { text, counterpartText };
}

// The recipient's half alone, for callers (and tests) that only need the one
// string. Delegates, so it can never drift from what is sent.
export function composeDirectiveText(input) {
  return composeDirective(input).text;
}

// ── WHAT THE OPERATOR MUST BE TOLD BEFORE SENDING ────
//
// Warnings, NOT silently disabled controls. A greyed Send with no explanation is
// a control that reads as broken; the page's own `composeBlockedReason` already
// makes this argument for the three ways a directive can be composed that nobody
// will ever see. These are the ways a directive can be SENT and be wrong.
//
// `blocking: true` refuses the send. Everything else is said out loud and the
// operator decides — they can see the room and the board, and this module cannot.
export function directiveWarnings({
  action = "",
  where = "",
  detail = "",
  position = "",
  recipientCount = 0,
} = {}) {
  const verb = actionById(action);
  if (!verb) return [];
  const out = [];
  const place = String(where || "").trim();

  // ⚠️ BLOCKING. A verb that cannot be obeyed without a place must not go out
  // without one.
  if (verb.needsWhere && !place) {
    out.push({
      code: "no-where",
      blocking: true,
      message:
        verb.whereKind === "campus"
          ? `"${verb.label}" needs a campus. Without it the instruction names nowhere to go.`
          : `"${verb.label}" needs a room. Without it the instruction names nowhere to go.`,
    });
  }

  // ⚠️ BLOCKING. See `needsDetail` on hold_room: a stop order without a cause
  // gets negotiated, and leaves the room with no next action.
  if (verb.needsDetail && !String(detail || "").trim()) {
    out.push({
      code: "no-detail",
      blocking: true,
      message: `"${verb.label}" needs a reason. A stop order without a cause gets argued with at the door.`,
    });
  }

  // ⚠️ BLOCKING. A call change that names no call is the "your room" mistake in
  // another field.
  if (verb.needsPosition && !String(position || "").trim()) {
    out.push({
      code: "no-position",
      blocking: true,
      message: `"${verb.label}" needs a call position. The board holds the call list; the message must name which call.`,
    });
  }

  // ⚠️ NOT BLOCKING, ON PURPOSE. Two people told to take one seat is usually a
  // mistake and occasionally deliberate (two CRNAs to one long case, or an
  // orienting pair, which genuinely is two people in one room). The board runner
  // can see which; this module cannot. So it is named loudly and left to them.
  if (verb.seats === 1 && recipientCount > 1) {
    out.push({
      code: "single-seat",
      blocking: false,
      message: `"${verb.label}" names one seat. All ${recipientCount} people will each be told to take it.`,
    });
  }

  return out;
}

// Whether the warnings above permit a send at all. Kept separate from `canSend`
// in domain/tasks (which asks "is there a message, and somebody to send it to")
// because these are questions about the SENTENCE, not about the payload.
export function directiveBlocked(input) {
  return directiveWarnings(input).some((w) => w.blocking);
}
